import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  fetchPuzzle,
  fetchTodayPuzzles,
  fetchMyStreaks,
  isLockedPuzzle,
  type Difficulty,
  type TileDto,
  type PublicPuzzle,
} from '../api/puzzles';
import { startLife, submitMove, finishLife, newIdempotencyKey, type LifeState } from '../api/lives';
import { apiErrorCode, apiErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  useGameStore,
  availableRack,
  snapshotFromServerLife,
  preserveRackOrder,
} from '../game/store';
import { loadDictionary } from '../game/dictionary';
import { loadLocalLife, saveLocalLife } from '../game/local-persistence';
import { recordAnonPlay, claimBoard, isDayClaimed } from '../api/anon';
import { previewMove } from '../game/score-preview';
import { buildShareText } from '../game/share';
import { formatDateUK } from '../lib/format';
import { Board } from '../components/board/Board';
import { Rack } from '../components/game/Rack';
import { ScoreBar } from '../components/game/ScoreBar';
import { ActionRow } from '../components/game/ActionRow';
import { BlankPicker } from '../components/game/BlankPicker';
import {
  GameDndProvider,
  DraggableTile,
  DroppableCell,
  RackDropSlot,
} from '../components/game/dnd';
import { BottomBar } from '../components/layout/BottomBar';
import { EndOfLifeModal } from '../components/modals/EndOfLifeModal';
import { EndOfDayModal } from '../components/modals/EndOfDayModal';
import { SolutionModal } from '../components/game/SolutionModal';

const DIFFICULTIES = new Set(['EASY', 'MEDIUM', 'HARD']);

export default function Game() {
  const params = useParams<{ difficulty: string; date?: string }>();
  const difficulty = (params.difficulty ?? '').toUpperCase() as Difficulty;

  if (!DIFFICULTIES.has(difficulty)) {
    return (
      <section data-testid="page-game">
        <p role="alert" className="text-red-600">
          Unknown difficulty. Pick a puzzle from the landing page.
        </p>
      </section>
    );
  }
  return <GameScreen difficulty={difficulty} date={params.date} />;
}

function GameScreen({ difficulty, date }: { difficulty: Difficulty; date?: string }) {
  const store = useGameStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const [blankCell, setBlankCell] = useState<{ row: number; col: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [dayBlocked, setDayBlocked] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [showSolution, setShowSolution] = useState(false);

  // Past-date puzzles are practice mode — always local, never persisted (spec 10.1).
  const isPractice = !!date;
  const serverMode = !!user && !isPractice;

  const puzzleQuery = useQuery({
    queryKey: ['puzzle', date ?? 'today', difficulty],
    queryFn: async () => {
      if (date) return fetchPuzzle(date, difficulty);
      const today = await fetchTodayPuzzles();
      const p = today.puzzles.find((x) => x.difficulty === difficulty);
      if (!p) throw new Error('No puzzle for today');
      return p;
    },
  });

  const dictQuery = useQuery({
    queryKey: ['dictionary'],
    queryFn: loadDictionary,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const streaksQuery = useQuery({
    queryKey: ['me', 'streaks'],
    queryFn: fetchMyStreaks,
    enabled: !!user,
  });

  const puzzle = puzzleQuery.data;
  const hydratedFor = useRef<string | null>(null);

  // Hydrate once per (puzzle, mode): server lives resume from the backend,
  // guests/practice play entirely in the store.
  useEffect(() => {
    if (!puzzle || authLoading || isLockedPuzzle(puzzle)) return;
    const hydrationKey = `${puzzle.id}:${serverMode ? 'server' : 'local'}`;
    if (hydratedFor.current === hydrationKey) return;
    hydratedFor.current = hydrationKey;
    setModalDismissed(false);
    setShowShare(false);
    setDayBlocked(null);

    if (serverMode) {
      startLife(puzzle.puzzle_date, difficulty)
        .then((life) => {
          useGameStore.getState().hydrate({
            ...snapshotFromServerLife(life),
            puzzle,
            difficulty,
          });
        })
        .catch((err) => {
          const code = apiErrorCode(err);
          if (code === 'NO_LIVES_REMAINING') setDayBlocked('All three lives are used for today.');
          else toast.error(apiErrorMessage(err, "Couldn't start the game"));
        });
    } else {
      // Guest play survives refreshes: restore today's saved state when it
      // exists (practice/past puzzles stay ephemeral).
      // A claimed day's save is a duplicate of what now lives on the player's
      // account, so it must not be restored onto the board — the landing cards
      // and the leaderboard teaser ignore it for the same reason. Any play
      // after a claim starts fresh.
      const saved =
        isPractice || isDayClaimed(puzzle.puzzle_date) ? null : loadLocalLife(puzzle.id);
      useGameStore.getState().hydrate({
        puzzle,
        difficulty,
        board: saved?.board ?? puzzle.starting_board,
        rack: saved?.rack ?? puzzle.daily_bag.slice(0, 7),
        bag: saved?.bag ?? puzzle.daily_bag.slice(7),
        cumulativeScore: saved?.cumulativeScore ?? 0,
        movesUsed: saved?.movesUsed ?? 0,
        moveHistory: saved?.moveHistory ?? [],
        previousLives: saved?.previousLives ?? [],
        lifeNumber: saved?.lifeNumber ?? 1,
        status: saved?.status ?? 'IN_PROGRESS',
        localMode: true,
        dayWon: saved?.dayWon ?? false,
      });
      if (saved && saved.staged.length > 0) {
        useGameStore.setState({ staged: saved.staged });
      }
    }
  }, [puzzle, authLoading, serverMode, difficulty, isPractice]);

  // Persist guest progress (moves, lives used, staged tiles) across refreshes.
  useEffect(() => {
    if (!puzzle || isPractice || serverMode || isLockedPuzzle(puzzle)) return;
    return useGameStore.subscribe((s) => {
      if (s.localMode && s.puzzle?.id === puzzle.id) {
        saveLocalLife(puzzle.id, puzzle.puzzle_date, s);
      }
    });
  }, [puzzle, isPractice, serverMode]);

  // Guest play analytics (fire-and-forget, deduped server-side). Fires for the
  // current life of today's puzzle in guest mode only — logged-in users already
  // produce Life rows, and practice/past puzzles are excluded. Re-fires when the
  // guest advances to a new life; refreshes hit the same tuple and are ignored.
  useEffect(() => {
    if (!puzzle || serverMode || isPractice || isLockedPuzzle(puzzle)) return;
    if (store.puzzle?.id !== puzzle.id) return;
    void recordAnonPlay(puzzle.puzzle_date, difficulty, store.lifeNumber);
  }, [puzzle, serverMode, isPractice, difficulty, store.puzzle?.id, store.lifeNumber]);

  // Commit the guest to this board on their first move of the day. Fails open:
  // if the call never lands, nothing locks and play continues.
  const boardClaimed = useRef(false);
  useEffect(() => {
    if (!puzzle || serverMode || isPractice || isLockedPuzzle(puzzle)) return;
    if (store.puzzle?.id !== puzzle.id) return;
    if (boardClaimed.current) return;
    // A reset with no tiles placed is inspection, not play — count actual
    // submitted moves, not lives sealed (a life can end with zero moves).
    const played =
      store.moveHistory.length > 0 || store.previousLives.some((l) => l.moves.length > 0);
    if (!played) return;
    boardClaimed.current = true;
    void claimBoard(puzzle.puzzle_date, difficulty).then(() => {
      // The entitlement just changed server-side, so every cached view of
      // today's boards is stale: the landing cards and the leaderboard
      // teaser (['puzzles','today']) and each board's own query (['puzzle',
      // 'today', difficulty]). Without this they keep serving the pre-lock
      // payload until staleTime expires, so a guest navigating straight back
      // sees no locks until they reload.
      void queryClient.invalidateQueries({ queryKey: ['puzzles', 'today'] });
      void queryClient.invalidateQueries({ queryKey: ['puzzle'] });
    });
  }, [
    puzzle,
    serverMode,
    isPractice,
    difficulty,
    queryClient,
    store.puzzle?.id,
    store.moveHistory.length,
    store.previousLives.length,
  ]);

  const rackTiles = availableRack(store);
  const dict = dictQuery.data;

  const preview = useMemo(
    () => (dict && store.staged.length > 0 ? previewMove(store.board, store.staged, dict) : null),
    [dict, store.board, store.staged],
  );

  // Live word-check board highlights: every cell of each formed word goes
  // green/red; on a structural error the staged tiles go red.
  const highlights = useMemo(() => {
    const map = new Map<string, 'valid' | 'invalid'>();
    if (!preview) return map;
    if (preview.message) {
      for (const p of store.staged) map.set(`${p.row},${p.col}`, 'invalid');
      return map;
    }
    for (const w of preview.words) {
      for (const c of w.cells) {
        const key = `${c.row},${c.col}`;
        // A cell shared by a valid and an invalid word reads as invalid.
        if (!w.valid || map.get(key) !== 'invalid') {
          map.set(key, w.valid ? 'valid' : 'invalid');
        }
      }
    }
    return map;
  }, [preview, store.staged]);

  function placeTile(row: number, col: number, tile: TileDto, from?: { row: number; col: number }) {
    if (store.status !== 'IN_PROGRESS') return;
    // Dragging a tile already on the board: reposition or swap, keep its letter.
    if (from) {
      store.moveStaged(from.row, from.col, row, col);
      return;
    }
    store.addPlacement(row, col, tile);
    if (tile.is_blank) setBlankCell({ row, col });
  }

  function onCellTap(row: number, col: number) {
    const stagedHere = store.staged.find((p) => p.row === row && p.col === col);
    if (stagedHere) {
      store.removePlacement(row, col);
      return;
    }
    const selected = rackTiles.find((t) => t.tile_id === store.selectedTileId);
    if (selected) placeTile(row, col, selected);
  }

  function reconcile(life: LifeState) {
    const snapshot = snapshotFromServerLife(life);
    useGameStore.getState().hydrate({
      ...snapshot,
      // Don't shuffle the player's hand arrangement on every server response.
      rack: preserveRackOrder(useGameStore.getState().rack, snapshot.rack ?? []),
      // reconcile() only runs from handlers reachable once the render below has
      // already passed the isLockedPuzzle guard, so puzzle is never locked here.
      puzzle: puzzle as PublicPuzzle,
      difficulty,
    });
    setModalDismissed(false);
    // The landing page's per-difficulty state is now stale.
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  }

  /** A life just ended, so this month's points changed. Refresh the home
   *  strip's season query — under the app's global 30s staleTime it would
   *  otherwise keep showing pre-game points to anyone who goes straight back
   *  to the landing page.
   *
   *  Called at each life-ending site rather than from reconcile(): on reset,
   *  reconcile receives the NEXT life (already IN_PROGRESS again), so a
   *  status check there silently misses the most common ending of all — the
   *  player who resets a board they aren't winning. */
  function onLifeEnded() {
    void queryClient.invalidateQueries({ queryKey: ['seasons', 'current'] });
    // The same event moves the player's daily rank and their own public
    // profile. Both are reachable in a couple of taps from the end-of-life
    // modal, well inside the 30s staleTime, so neither can be left to expire.
    void queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
    void queryClient.invalidateQueries({ queryKey: ['player'] });
  }

  async function onSubmit() {
    if (!preview?.valid || store.status !== 'IN_PROGRESS') return;
    if (!serverMode) {
      store.applyLocalMove(preview.score, preview.words[0]?.word ?? '?');
      return;
    }
    if (!store.lifeId) return;
    setSubmitting(true);
    try {
      const result = await submitMove(
        store.lifeId,
        store.staged,
        preview.score,
        newIdempotencyKey(),
      );
      reconcile(result.life);
      // A move can end the life outright — the last move played, or a loss.
      if (result.life.status !== 'IN_PROGRESS') onLifeEnded();
      void streaksQuery.refetch();
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === 'SCORE_MISMATCH') {
        toast.error('Score mismatch with the server — please refresh the page.');
      } else if (code === 'LIFE_ADVANCED' || code === 'LIFE_NOT_IN_PROGRESS') {
        toast.error('This life moved on in another tab — reloading the latest state.');
        const fresh = await startLife(puzzle!.puzzle_date, difficulty).catch(() => null);
        if (fresh) reconcile(fresh);
      } else {
        toast.error(apiErrorMessage(err, "Couldn't submit the move"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onBank() {
    if (store.status !== 'IN_PROGRESS') return;
    if (!serverMode) {
      store.finishLocalLife();
      return;
    }
    if (!store.lifeId) return;
    try {
      const life = await finishLife(store.lifeId);
      reconcile(life);
      onLifeEnded();
      void streaksQuery.refetch();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't bank the score"));
    }
  }

  /** Reset: forfeit the current life and start fresh on the next one. */
  async function onResetConfirmed() {
    setConfirmReset(false);
    if (store.status !== 'IN_PROGRESS') return;
    if (!serverMode) {
      store.finishLocalLife();
      store.startNextLocalLife(); // no-op on life 3 → the end-of-day modal shows
      return;
    }
    if (!store.lifeId) return;
    try {
      const finished = await finishLife(store.lifeId);
      onLifeEnded();
      void streaksQuery.refetch();
      if (finished.life_number < 3) {
        reconcile(await startLife(puzzle!.puzzle_date, difficulty));
      } else {
        reconcile(finished);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't reset the board"));
    }
  }

  async function onTryAgain() {
    setModalDismissed(false);
    setShowShare(false);
    if (!serverMode) {
      store.startNextLocalLife();
      return;
    }
    try {
      const life = await startLife(puzzle!.puzzle_date, difficulty);
      reconcile(life);
    } catch (err) {
      const code = apiErrorCode(err);
      if (code === 'NO_LIVES_REMAINING') setDayBlocked('All three lives are used for today.');
      else toast.error(apiErrorMessage(err, "Couldn't start a new life"));
    }
  }

  if (puzzleQuery.isLoading || authLoading) {
    return (
      <section data-testid="page-game">
        <p role="status" className="text-stone-500">
          Loading puzzle…
        </p>
      </section>
    );
  }
  if (puzzleQuery.isError || !puzzle) {
    return (
      <section data-testid="page-game">
        <p role="alert" className="text-red-600">
          Couldn't load this puzzle — it may not exist for {date ?? 'today'}.
        </p>
      </section>
    );
  }
  // Signed out, but this browser already handed today's run to an account.
  // Any lives left on this board live on that account, and guest play cannot
  // reach them — so offering a board here would hand them a game that could
  // never be saved. Send them to sign in instead.
  if (!user && !isPractice && isDayClaimed(puzzle.puzzle_date)) {
    return (
      <section data-testid="claimed-signed-out" className="pt-8 text-center">
        <p className="text-lg font-semibold">Today's game is on your account</p>
        <p className="mx-auto mt-2 max-w-sm text-sm text-stone-600">
          You've already saved today's play to an account. Sign in to pick up where you left off —
          playing signed out wouldn't be saved.
        </p>
        <a
          href="/login"
          className="mx-auto mt-5 block w-full max-w-xs rounded-md border-b-4 border-emerald-950 bg-emerald-800 px-4 py-2.5 text-sm font-bold uppercase tracking-[0.18em] text-white"
        >
          Sign in
        </a>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mx-auto mt-4 block text-sm text-stone-600 underline"
        >
          Back to today
        </button>
      </section>
    );
  }
  if (isLockedPuzzle(puzzle)) {
    return (
      <section data-testid="locked-board" className="pt-8 text-center">
        <p className="text-lg font-semibold">
          {difficulty.charAt(0) + difficulty.slice(1).toLowerCase()} is locked today
        </p>
        <p className="mx-auto mt-2 max-w-sm text-sm text-stone-600">
          You get one board a day as a guest. Create a free account to play all three, keep your
          streak, and appear on the leaderboard.
        </p>
        <a
          href="/register"
          className="mx-auto mt-5 block w-full max-w-xs rounded-md border-b-4 border-emerald-950 bg-emerald-800 px-4 py-2.5 text-sm font-bold uppercase tracking-[0.18em] text-white"
        >
          Create free account
        </a>
        <Link to="/leaderboard" className="mt-3 inline-block text-sm text-emerald-800 underline">
          See today's leaderboard
        </Link>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mx-auto mt-4 block text-sm text-stone-600 underline"
        >
          Back to today
        </button>
      </section>
    );
  }
  if (dayBlocked) {
    return (
      <section data-testid="page-game" className="pt-8 text-center">
        <p className="text-lg">{dayBlocked}</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mt-4 rounded-md bg-emerald-700 px-4 py-2 font-medium text-white"
        >
          Back to today
        </button>
        <button
          type="button"
          onClick={() => setShowSolution(true)}
          className="mx-auto mt-3 block rounded-md border border-emerald-700 px-4 py-2 font-medium text-emerald-800"
        >
          See what was possible
        </button>
        {showSolution && puzzle ? (
          <SolutionModal
            date={puzzle.puzzle_date}
            difficulty={difficulty}
            ownScore={store.cumulativeScore}
            onClose={() => setShowSolution(false)}
          />
        ) : null}
      </section>
    );
  }

  const lifeOver = store.status === 'WON' || store.status === 'LOST';
  const livesLeft = 3 - store.lifeNumber;
  const dayOver = store.status === 'WON' || (store.status === 'LOST' && livesLeft === 0);
  const streak =
    streaksQuery.data?.streaks.find((s) => s.difficulty === difficulty)?.current_streak ?? null;
  const shareText = buildShareText({
    date: puzzle.puzzle_date,
    difficulty,
    won: store.status === 'WON' || store.dayWon,
    score: store.cumulativeScore,
    target: puzzle.target_score,
    livesUsed: store.lifeNumber,
    streak,
    board: store.board,
  });

  return (
    <GameDndProvider>
      {/* pb clears the fixed bottom bar (~110px) without overflowing short
          mobile viewports — overflow breaks fixed-element hit-testing under
          mobile emulation. */}
      <section data-testid="page-game" className="pb-28">
        <header className="flex items-center justify-between">
          <h1 className="text-lg font-bold capitalize">
            {difficulty.toLowerCase()} · {formatDateUK(puzzle.puzzle_date)}
            {isPractice ? ' (practice)' : ''}
          </h1>
          <p role="img" aria-label={`Life ${store.lifeNumber} of 3`} className="text-sm">
            {Array.from({ length: 3 }, (_, i) => (
              <span key={i} aria-hidden="true">
                {i < 3 - (store.lifeNumber - 1) ? '❤️' : '🤍'}
              </span>
            ))}
          </p>
        </header>

        <div className="mt-3">
          <ScoreBar
            cumulative={store.cumulativeScore}
            target={puzzle.target_score}
            movesUsed={store.movesUsed}
            moveCount={puzzle.move_count}
            preview={preview}
          />
        </div>

        <div className="board-fit mt-3">
          <Board
            board={store.board}
            staged={store.staged}
            onCellTap={onCellTap}
            highlights={highlights}
            cellWrapper={(row, col, cell) => {
              const stagedHere = store.staged.find((p) => p.row === row && p.col === col);
              const content =
                stagedHere && store.status === 'IN_PROGRESS' ? (
                  <DraggableTile
                    tile={{
                      tile_id: stagedHere.tile_id,
                      letter: stagedHere.is_blank ? '' : stagedHere.letter,
                      is_blank: stagedHere.is_blank,
                    }}
                    from={{ row, col }}
                  >
                    {cell}
                  </DraggableTile>
                ) : (
                  cell
                );
              return (
                <DroppableCell row={row} col={col} onDropTile={placeTile}>
                  {content}
                </DroppableCell>
              );
            }}
          />
        </div>

        <p className="mt-3 text-center">
          <button
            type="button"
            onClick={() => setConfirmReset(true)}
            disabled={store.status !== 'IN_PROGRESS'}
            className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:border-red-300 hover:bg-red-100 disabled:opacity-40"
          >
            <span aria-hidden="true">↺</span> Reset board (uses a life)
          </button>
        </p>

        <BottomBar>
          <Rack
            tiles={rackTiles}
            selectedTileId={store.selectedTileId}
            onTileTap={(tile) =>
              store.selectTile(store.selectedTileId === tile.tile_id ? null : tile.tile_id)
            }
            tileWrapper={(tile, button) => <DraggableTile tile={tile}>{button}</DraggableTile>}
            slotWrapper={(index, _tile, node) => (
              <RackDropSlot
                index={index}
                onDropTile={(dragged, toIndex, from) =>
                  from
                    ? store.removePlacement(from.row, from.col) // drag off the board → recall
                    : store.reorderRack(dragged.tile_id, toIndex)
                }
              >
                {node}
              </RackDropSlot>
            )}
          />
          <div className="mt-2">
            <ActionRow
              lifeNumber={store.lifeNumber}
              hasStaged={store.staged.length > 0}
              canSubmit={(preview?.valid ?? false) && store.status === 'IN_PROGRESS'}
              submitting={submitting}
              canBank={
                store.status === 'IN_PROGRESS' &&
                store.cumulativeScore >= (puzzle.target_score ?? Infinity)
              }
              onRecall={store.recallAll}
              onUndo={store.undoLast}
              onSubmit={onSubmit}
              onBank={onBank}
            />
          </div>
        </BottomBar>

        {confirmReset ? (
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Reset board"
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
            onClick={() => setConfirmReset(false)}
          >
            <div
              className="w-full max-w-sm rounded-xl bg-white p-6 text-center shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold">Reset the board?</h2>
              <p className="mt-2 text-sm text-stone-600">
                Resetting forfeits this life — that's one of your three for this challenge
                {store.lifeNumber >= 3 ? ", and it's your last one" : ''}.
              </p>
              <div className="mt-5 flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmReset(false)}
                  className="flex-1 rounded-md border border-stone-300 px-4 py-2 font-medium text-stone-700 hover:bg-stone-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onResetConfirmed}
                  className="flex-1 rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {blankCell ? (
          <BlankPicker
            onPick={(letter) => {
              store.designateBlank(blankCell.row, blankCell.col, letter);
              setBlankCell(null);
            }}
            onCancel={() => {
              store.removePlacement(blankCell.row, blankCell.col);
              setBlankCell(null);
            }}
          />
        ) : null}

        {lifeOver && !modalDismissed && !showShare ? (
          <EndOfLifeModal
            won={store.status === 'WON'}
            score={store.cumulativeScore}
            target={puzzle.target_score}
            lifeNumber={store.lifeNumber}
            livesLeft={livesLeft}
            board={store.board}
            moves={store.moveHistory}
            onTryAgain={onTryAgain}
            onShare={() => setShowShare(true)}
            onBackToToday={() => navigate('/')}
            onSeeSolution={serverMode && livesLeft === 0 ? () => setShowSolution(true) : undefined}
          >
            {!user ? (
              <div className="mt-3 space-y-2 text-sm text-emerald-900">
                {store.status === 'LOST' ? (
                  <p className="font-semibold">
                    <a href="/register" className="underline">
                      Create an account
                    </a>{' '}
                    to see the optimised daily puzzle.
                  </p>
                ) : null}
                <p>
                  {dayOver && store.status === 'WON' ? "You completed today's challenge — " : ''}
                  <a href="/login" className="font-semibold underline">
                    sign in
                  </a>{' '}
                  to save your streak and history.
                </p>
                <p>
                  <Link to="/leaderboard" className="font-semibold underline">
                    See where {store.cumulativeScore} puts you today →
                  </Link>
                </p>
              </div>
            ) : null}
          </EndOfLifeModal>
        ) : null}

        {showShare ? (
          <EndOfDayModal
            shareText={shareText}
            isGuest={!user}
            onClose={() => {
              setShowShare(false);
              setModalDismissed(true);
            }}
          />
        ) : null}

        {showSolution ? (
          <SolutionModal
            date={puzzle.puzzle_date}
            difficulty={difficulty}
            ownScore={store.cumulativeScore}
            onClose={() => setShowSolution(false)}
          />
        ) : null}
      </section>
    </GameDndProvider>
  );
}
